import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PolicyService } from '../../Services/policy.service';
import { AuthenticationService } from '../../Services/authentication.service';
import { Policy } from '../../Models/policy.model';

@Component({
    selector: 'app-policy-viewer',
    templateUrl: './policy-viewer.component.html',
    styleUrls: ['./policy-viewer.component.css'],
    encapsulation: ViewEncapsulation.None
})
export class PolicyViewerComponent implements OnInit, OnDestroy {
    @ViewChild('pdfContainer') pdfContainer: ElementRef;

    policyId: number;
    policyName: string = 'Policy Document';

    loading: boolean = false;
    errorMessage: string = '';
    infoMessage: string = '';

    currentPage: number = 1;
    totalPages: number = 0;
    zoomPercent: number = 150;
    searchQuery: string = '';
    searchMatches: number[] = [];
    currentMatchIndex: number = -1;
    isSearchIndexing: boolean = false;
    private readonly maxDevicePixelRatio: number = 2;
    private pageTextIndex: { page: number; text: string }[] = [];
    private textLayerByPage: { [pageNumber: number]: HTMLElement } = {};
    private searchLayerByPage: { [pageNumber: number]: HTMLElement } = {};
    private annotationLayerByPage: { [pageNumber: number]: HTMLElement } = {};
    private highlightedMatches: { page: number; element: HTMLElement }[] = [];
    private isProgrammaticScrollInProgress: boolean = false;
    private pendingNavigationPage: number = 0;
    private scrollSyncTimeoutRef: any = null;
    private searchIndexPromise: Promise<void> = Promise.resolve();
    private searchIndexBuiltForPageCount: number = 0;

    private objectUrl: string = null;
    private pdfDoc: any = null;
    private isFitWidth: boolean = false;

    private readonly keydownHandler = (event: KeyboardEvent) => this.handleBlockedShortcuts(event);
    private readonly contextMenuHandler = (event: MouseEvent) => event.preventDefault();
    private readonly resizeHandler = () => this.fitWidth();
    private readonly scrollHandler = () => this.updateCurrentPageByScroll();

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private policyService: PolicyService,
        private authenticationService: AuthenticationService
    ) { }

    ngOnInit() {
        this.policyId = +this.route.snapshot.params['id'];
        this.loadPolicyNameFromApi();
        this.toggleOuterScrollLock(true);

        document.addEventListener('contextmenu', this.contextMenuHandler);
        window.addEventListener('keydown', this.keydownHandler, true);
        window.addEventListener('resize', this.resizeHandler);

        this.openPolicy();
    }

    private loadPolicyNameFromApi() {
        this.policyService.getPolicies().subscribe(
            (policies: Policy[]) => {
                const matchedPolicy = (policies || []).find(policy => policy.policyID === this.policyId);
                if (matchedPolicy && matchedPolicy.policyName) {
                    this.policyName = matchedPolicy.policyName;
                }
            },
            () => {
                // Keep default title when name lookup fails; openPolicy() handles primary API errors.
            }
        );
    }

    // Critical flow: request secure PDF blob and render it through PDF.js.
    private openPolicy() {
        this.loading = true;
        this.errorMessage = '';

        this.policyService.viewPolicy(this.policyId).subscribe(
            
            blob => {
                this.objectUrl = URL.createObjectURL(blob);
                this.loadPdf(this.objectUrl);
                        console.log(blob);
        console.log(blob.size);
        console.log(blob.type);

            },
            error => {
                this.loading = false;
                this.handleHttpError(error);
            }
            
        );
    }

    // Critical PDF.js integration: render all pages as canvas to prevent native browser controls.
    private loadPdf(url: string) {
        try {
            const pdfLib = this.getPdfLibrary();
            if (!pdfLib) {
                this.loading = false;
                this.errorMessage = 'PDF viewer library is not loaded.';
                console.error('PDF.js global not found. Expected one of window.pdfjsDistBuildPdf or window.PDFJS.');
                return;
            }

            if (pdfLib.PDFJS && typeof pdfLib.PDFJS.workerSrc !== 'undefined') {
                console.log('Setting PDFJS workerSrc to assets/build/pdf.worker.js');
                pdfLib.PDFJS.workerSrc = 'assets/build/pdf.worker.js';
            }
            if (typeof pdfLib.workerSrc !== 'undefined') {
                console.log('Setting PDF.js workerSrc to assets/build/pdf.worker.js');
                pdfLib.workerSrc = 'assets/build/pdf.worker.js';
            }

            const getDocumentFn = (pdfLib.getDocument) || (pdfLib.PDFJS && pdfLib.PDFJS.getDocument);
            if (!getDocumentFn) {
                this.loading = false;
                this.errorMessage = 'PDF viewer library is not loaded.';
                console.error('PDF.js getDocument function not found on resolved library object.');
                return;
            }

            getDocumentFn(url).promise.then((documentRef: any) => {
                this.pdfDoc = documentRef;
                this.totalPages = this.pdfDoc.numPages;
                this.buildSearchIndex();
                this.renderAllPages();
                
               
                
            }).catch((pdfLoadError: any) => {
                this.loading = false;
                console.error('Policy PDF load error:', pdfLoadError);
                this.errorMessage = this.extractErrorMessage(pdfLoadError) || 'Failed to load policy document.';
            });
        } catch (error) {
            this.loading = false;
            console.error('Policy PDF processing error:', error);
            this.errorMessage = this.extractErrorMessage(error) || 'Failed to load policy document.';
        }
    }

    private renderAllPages() {
        if (!this.pdfDoc || !this.pdfContainer || !this.pdfContainer.nativeElement) {
            this.loading = false;
            return;
        }

        const container: HTMLElement = this.pdfContainer.nativeElement;
        container.innerHTML = '';
        this.textLayerByPage = {};
        this.searchLayerByPage = {};
        this.annotationLayerByPage = {};

        let pagePromises: Promise<any>[] = [];
        for (let pageNumber = 1; pageNumber <= this.pdfDoc.numPages; pageNumber++) {
            pagePromises.push(this.renderPage(pageNumber));
        }

        Promise.all(pagePromises).then(() => {
            this.loading = false;
            container.removeEventListener('scroll', this.scrollHandler);
            container.addEventListener('scroll', this.scrollHandler);
            this.currentPage = 1;

            if (this.searchQuery && this.searchQuery.trim()) {
                this.runSearch(false);
            }
        });
    }

    private renderPage(pageNumber: number): Promise<any> {
        return this.pdfDoc.getPage(pageNumber).then((page: any) => {
            const container: HTMLElement = this.pdfContainer.nativeElement;
            const baseViewport = page.getViewport(1);
            const scale = this.isFitWidth ? this.getFitScale(baseViewport.width, container.clientWidth) : (this.zoomPercent / 100);
            const viewport = page.getViewport(scale);
            const outputScale = Math.min(window.devicePixelRatio || 1, this.maxDevicePixelRatio);

            const pageWrapper = document.createElement('div');
            pageWrapper.className = 'pdf-page-wrapper';
            pageWrapper.id = 'policy-page-' + pageNumber;
            pageWrapper.style.position = 'relative';
            pageWrapper.style.display = 'inline-block';
            pageWrapper.style.width = viewport.width + 'px';
            pageWrapper.style.height = viewport.height + 'px';

            const canvas = document.createElement('canvas');
            canvas.className = 'pdf-page-canvas';
            canvas.setAttribute('aria-label', 'Policy page ' + pageNumber);
            canvas.width = Math.floor(viewport.width * outputScale);
            canvas.height = Math.floor(viewport.height * outputScale);
            canvas.style.width = '100%';
            canvas.style.height = '100%';
            canvas.style.display = 'block';

            const context = canvas.getContext('2d');
            const contextAny = context as any;
            if (contextAny && typeof contextAny.imageSmoothingEnabled !== 'undefined') {
                contextAny.imageSmoothingEnabled = true;
            }
            pageWrapper.appendChild(canvas);

            const annotationLayer = document.createElement('div');
            annotationLayer.className = 'annotationLayer';
            annotationLayer.style.position = 'absolute';
            annotationLayer.style.top = '0';
            annotationLayer.style.left = '0';
            annotationLayer.style.right = '0';
            annotationLayer.style.bottom = '0';
            annotationLayer.style.overflow = 'hidden';
            annotationLayer.style.zIndex = '6';
            pageWrapper.appendChild(annotationLayer);

            const textLayer = document.createElement('div');
            textLayer.className = 'textLayer policy-text-layer';
            textLayer.style.position = 'absolute';
            textLayer.style.top = '0';
            textLayer.style.left = '0';
            textLayer.style.right = '0';
            textLayer.style.bottom = '0';
            textLayer.style.overflow = 'hidden';
            textLayer.style.zIndex = '4';
            pageWrapper.appendChild(textLayer);

            const searchLayer = document.createElement('div');
            searchLayer.className = 'policy-search-layer';
            searchLayer.style.position = 'absolute';
            searchLayer.style.top = '0';
            searchLayer.style.left = '0';
            searchLayer.style.right = '0';
            searchLayer.style.bottom = '0';
            searchLayer.style.overflow = 'hidden';
            searchLayer.style.zIndex = '5';
            searchLayer.style.pointerEvents = 'none';
            pageWrapper.appendChild(searchLayer);

            container.appendChild(pageWrapper);

            const pdfLib = this.getPdfLibrary();
            const renderContext: any = {
                canvasContext: context,
                viewport: viewport
            };
            if (outputScale !== 1) {
                renderContext.transform = [outputScale, 0, 0, outputScale, 0, 0];
            }

            const renderPromise = page.render(renderContext).promise;
            const textLayerPromise = this.renderTextLayer(page, viewport, textLayer, pdfLib)
                .then(() => {
                    this.textLayerByPage[pageNumber] = textLayer;
                    this.searchLayerByPage[pageNumber] = searchLayer;
                    this.annotationLayerByPage[pageNumber] = annotationLayer;
                });
            const annotationPromise = this.renderAnnotationLayer(page, viewport, annotationLayer, pdfLib);

            return Promise.all([renderPromise, annotationPromise, textLayerPromise]).then(() => undefined);
        });
    }

    private renderTextLayer(page: any, viewport: any, textLayer: HTMLElement, pdfLib: any): Promise<void> {
        if (!page || !textLayer || !pdfLib || !pdfLib.renderTextLayer) {
            return Promise.resolve();
        }

        return page.getTextContent()
            .then((textContent: any) => {
                textLayer.innerHTML = '';

                const textRenderTask = pdfLib.renderTextLayer({
                    textContent: textContent,
                    container: textLayer,
                    viewport: viewport,
                    textDivs: [],
                    enhanceTextSelection: false,
                    timeout: 0
                });

                return textRenderTask.promise.then(() => {
                    const textElements = textLayer.querySelectorAll('span, div');
                    for (let index = 0; index < textElements.length; index++) {
                        const textElement = textElements[index] as HTMLElement;
                        const rawText = textElement.textContent || '';
                        textElement.setAttribute('data-raw-text', rawText);
                    }
                });
            })
            .catch(() => {
                textLayer.innerHTML = '';
            });
    }

    private renderAnnotationLayer(page: any, viewport: any, annotationLayer: HTMLElement, pdfLib: any): Promise<void> {
        const annotationApi = pdfLib && pdfLib.AnnotationLayer ? pdfLib.AnnotationLayer : null;
        if (!annotationApi || !page || !annotationLayer) {
            return Promise.resolve();
        }

        return page.getAnnotations({ intent: 'display' }).then((annotations: any[]) => {
            console.log('Policy page ' + page.pageNumber + ' annotation count:', (annotations || []).length);
            console.log('Policy page ' + page.pageNumber + ' annotations:', annotations || []);

            const parameters = {
                viewport: viewport.clone({ dontFlip: true }),
                div: annotationLayer,
                annotations: annotations || [],
                page: page,
                renderInteractiveForms: false,
                linkService: this.getAnnotationLinkService(),
                downloadManager: null
            };

            annotationLayer.innerHTML = '';
            annotationApi.render(parameters);

            const renderedAnchorElements = Array.prototype.slice.call(annotationLayer.querySelectorAll('a[href]')) as HTMLAnchorElement[];
            renderedAnchorElements.forEach((anchor: HTMLAnchorElement) => {
                const href = (anchor.getAttribute('href') || '').trim();
                if (/^https?:\/\//i.test(href)) {
                    anchor.target = '_blank';
                    anchor.rel = 'noopener noreferrer';
                }
            });

            const renderedLinks = annotationLayer.querySelectorAll('a');
            console.log('Policy page ' + page.pageNumber + ' rendered annotation links:', renderedLinks.length);
        }).catch((error: any) => {
            console.warn('Unable to render policy annotations:', error);
        });
    }

    private getAnnotationLinkService(): any {
        return {
            getDestinationHash: (destination: any) => {
                if (typeof destination === 'string' && destination.trim()) {
                    return '#' + encodeURIComponent(destination.trim());
                }
                return '#';
            },
            navigateTo: (destination: any) => {
                this.navigateToDestination(destination);
            },
            getAnchorUrl: (hash: string) => hash || '#',
            externalLinkTarget: 2,
            externalLinkRel: 'noopener noreferrer',
            externalLinkEnabled: true,
            addLinkAttributes: (link: HTMLAnchorElement, url: string) => {
                if (!link || !url) {
                    return;
                }
                link.href = url;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
            },
            executeNamedAction: () => {
                // No named actions are supported in this embedded viewer.
            }
        };
    }

    private navigateToDestination(destination: any) {
        this.resolveDestinationPageNumber(destination).then((pageNumber: number | null) => {
            if (!pageNumber) {
                return;
            }
            this.scrollToPage(pageNumber);
        }).catch((error: any) => {
            console.warn('Unable to navigate to PDF destination:', destination, error);
        });
    }

    private resolveDestinationPageNumber(destination: any): Promise<number | null> {
        if (!this.pdfDoc || !destination) {
            return Promise.resolve(null);
        }

        const resolveExplicitDestination = (explicitDestination: any): Promise<number | null> => {
            if (!explicitDestination || !explicitDestination.length) {
                return Promise.resolve(null);
            }

            const pageReference = explicitDestination[0];
            if (typeof pageReference === 'number') {
                return Promise.resolve(pageReference + 1);
            }

            if (this.pdfDoc.getPageIndex) {
                return this.pdfDoc.getPageIndex(pageReference)
                    .then((pageIndex: number) => pageIndex + 1)
                    .catch(() => null);
            }

            return Promise.resolve(null);
        };

        if (Array.isArray(destination)) {
            return resolveExplicitDestination(destination);
        }

        if (typeof destination === 'string' && this.pdfDoc.getDestination) {
            return this.pdfDoc.getDestination(destination)
                .then((explicitDestination: any) => resolveExplicitDestination(explicitDestination))
                .then((resolvedPage: number | null | Promise<number | null>) => Promise.resolve(resolvedPage))
                .catch(() => null);
        }

        return Promise.resolve(null);
    }

    previousPage() {
        const basePage = this.pendingNavigationPage || this.currentPage;
        if (basePage > 1) {
            this.scrollToPage(basePage - 1);
        }
    }

    nextPage() {
        const basePage = this.pendingNavigationPage || this.currentPage;
        if (basePage < this.totalPages) {
            this.scrollToPage(basePage + 1);
        }
    }

    runSearch(resetToFirstResult: boolean = true) {
        const normalizedQuery = (this.searchQuery || '').trim().toLowerCase();

        if (!normalizedQuery) {
            this.searchMatches = [];
            this.currentMatchIndex = -1;
            this.applySearchHighlights('');
            return;
        }

        if (this.isSearchIndexing) {
            this.searchIndexPromise.then(() => {
                if ((this.searchQuery || '').trim().toLowerCase() === normalizedQuery) {
                    this.runSearch(resetToFirstResult);
                }
            });
            return;
        }

        if (!this.pageTextIndex.length) {
            this.buildSearchIndex().then(() => {
                if ((this.searchQuery || '').trim().toLowerCase() === normalizedQuery) {
                    this.runSearch(resetToFirstResult);
                }
            });
            return;
        }

        this.applySearchHighlights(normalizedQuery);
        this.searchMatches = this.pageTextIndex
            .filter(indexRecord => indexRecord.text.indexOf(normalizedQuery) > -1)
            .map(indexRecord => indexRecord.page);

        if (!this.searchMatches.length) {
            this.currentMatchIndex = -1;
            return;
        }

        this.currentMatchIndex = resetToFirstResult ? 0 : Math.min(this.currentMatchIndex, this.searchMatches.length - 1);
        if (this.currentMatchIndex < 0) {
            this.currentMatchIndex = 0;
        }

        this.navigateToCurrentSearchResult();
    }

    nextMatch() {
        if (!this.searchMatches.length) {
            return;
        }

        this.currentMatchIndex = (this.currentMatchIndex + 1) % this.searchMatches.length;
        this.navigateToCurrentSearchResult();
    }

    previousMatch() {
        if (!this.searchMatches.length) {
            return;
        }

        this.currentMatchIndex = (this.currentMatchIndex - 1 + this.searchMatches.length) % this.searchMatches.length;
        this.navigateToCurrentSearchResult();
    }

    zoomIn() {
        this.isFitWidth = false;
        this.zoomPercent = Math.min(300, this.zoomPercent + 10);
        this.renderAllPages();
    }

    zoomOut() {
        this.isFitWidth = false;
        this.zoomPercent = Math.max(50, this.zoomPercent - 10);
        this.renderAllPages();
    }

    fitWidth() {
        this.isFitWidth = true;
        this.renderAllPages();
    }

    closeViewer() {
        this.router.navigate(['/policies']);
    }

    private scrollToPage(pageNumber: number) {
        if (!this.pdfContainer || !this.pdfContainer.nativeElement) {
            return;
        }

        const container: HTMLElement = this.pdfContainer.nativeElement;
        const pageElement = document.getElementById('policy-page-' + pageNumber) as HTMLElement;
        if (!pageElement) {
            return;
        }

        const containerRect = container.getBoundingClientRect();
        const pageRect = pageElement.getBoundingClientRect();
        const targetTop = container.scrollTop + (pageRect.top - containerRect.top) - 8;

        this.isProgrammaticScrollInProgress = true;
        this.pendingNavigationPage = pageNumber;
        if (this.scrollSyncTimeoutRef) {
            clearTimeout(this.scrollSyncTimeoutRef);
        }

        container.scrollTo({
            top: Math.max(0, targetTop),
            behavior: 'smooth'
        });

        this.currentPage = pageNumber;

        this.scrollSyncTimeoutRef = setTimeout(() => {
            this.isProgrammaticScrollInProgress = false;
            this.pendingNavigationPage = 0;
        }, 450);
    }

    private updateCurrentPageByScroll() {
        if (!this.pdfContainer || !this.pdfContainer.nativeElement) {
            return;
        }

        if (this.isProgrammaticScrollInProgress) {
            return;
        }

        const containerRect = this.pdfContainer.nativeElement.getBoundingClientRect();
        for (let pageNumber = 1; pageNumber <= this.totalPages; pageNumber++) {
            const pageElement = document.getElementById('policy-page-' + pageNumber);
            if (!pageElement) {
                continue;
            }
            const pageRect = pageElement.getBoundingClientRect();
            if (pageRect.top <= (containerRect.top + 80) && pageRect.bottom >= (containerRect.top + 80)) {
                this.currentPage = pageNumber;
                return;
            }
        }
    }

    // Critical restriction: block print/save shortcuts and right-click to reduce unauthorized actions.
    private handleBlockedShortcuts(event: KeyboardEvent) {
        const key = (event.key || '').toLowerCase();
        const isCtrl = event.ctrlKey;
        const isShift = event.shiftKey;

        const isBlocked =
            (isCtrl && key === 'p') ||
            (isCtrl && key === 's') ||
            (isCtrl && isShift && key === 's') ||
            (isCtrl && isShift && key === 'p');

        if (isBlocked) {
            event.preventDefault();
            event.stopPropagation();
            this.infoMessage = 'Printing and downloading are disabled for policy documents.';
            setTimeout(() => {
                this.infoMessage = '';
            }, 2500);
        }
    }

    private getFitScale(pageWidth: number, containerWidth: number): number {
        const availableWidth = Math.max(320, containerWidth - 24);
        return availableWidth / pageWidth;
    }

    private buildSearchIndex(): Promise<void> {
        if (!this.pdfDoc || !this.pdfDoc.numPages) {
            this.pageTextIndex = [];
            this.searchIndexBuiltForPageCount = 0;
            this.searchIndexPromise = Promise.resolve();
            return this.searchIndexPromise;
        }

        if (
            this.searchIndexBuiltForPageCount === this.pdfDoc.numPages &&
            this.pageTextIndex.length === this.pdfDoc.numPages
        ) {
            return this.searchIndexPromise;
        }

        this.isSearchIndexing = true;
        const textPromises: Promise<{ page: number; text: string }>[] = [];

        for (let pageNumber = 1; pageNumber <= this.pdfDoc.numPages; pageNumber++) {
            textPromises.push(
                this.pdfDoc.getPage(pageNumber)
                    .then((page: any) => page.getTextContent())
                    .then((textContent: any) => {
                        const pageText = ((textContent && textContent.items) || [])
                            .map((textItem: any) => (textItem && textItem.str) ? textItem.str : '')
                            .join(' ')
                            .toLowerCase();

                        return {
                            page: pageNumber,
                            text: pageText
                        };
                    })
                    .catch(() => {
                        return {
                            page: pageNumber,
                            text: ''
                        };
                    })
            );
        }

        this.searchIndexPromise = Promise.all(textPromises)
            .then((indexRecords: { page: number; text: string }[]) => {
                this.pageTextIndex = indexRecords.sort((firstPage, secondPage) => firstPage.page - secondPage.page);
                this.searchIndexBuiltForPageCount = this.pdfDoc ? this.pdfDoc.numPages : 0;
                this.isSearchIndexing = false;
            }, (error: any) => {
                this.isSearchIndexing = false;
                return Promise.reject(error);
            });

        return this.searchIndexPromise;
    }

    private applySearchHighlights(searchValue: string) {
        const normalizedQuery = (searchValue || '').trim().toLowerCase();
        const hasQuery = normalizedQuery.length > 0;
        this.highlightedMatches = [];

        for (const key in this.textLayerByPage) {
            if (!this.textLayerByPage.hasOwnProperty(key)) {
                continue;
            }

            const pageNumber = parseInt(key, 10);
            const textLayer = this.textLayerByPage[key];
            const searchLayer = this.searchLayerByPage[key];
            const annotationLayer = this.annotationLayerByPage[key];
            if (!textLayer) {
                continue;
            }

            if (searchLayer) {
                searchLayer.innerHTML = '';
            }

            if (annotationLayer) {
                const linkElements = annotationLayer.querySelectorAll('a[href]');
                for (let linkIndex = 0; linkIndex < linkElements.length; linkIndex++) {
                    const linkElement = linkElements[linkIndex] as HTMLElement;
                    linkElement.classList.remove('policy-link-hit');
                    linkElement.classList.remove('policy-link-hit-active');

                    if (!hasQuery) {
                        continue;
                    }

                    const href = (linkElement.getAttribute('href') || '').toLowerCase();
                    const text = (linkElement.textContent || '').toLowerCase();
                    if (href.indexOf(normalizedQuery) > -1 || text.indexOf(normalizedQuery) > -1) {
                        linkElement.classList.add('policy-link-hit');
                        this.highlightedMatches.push({
                            page: pageNumber,
                            element: linkElement
                        });
                    }
                }
            }

            const textElements = textLayer.querySelectorAll('span[data-raw-text], div[data-raw-text]');
            const textLayerRect = textLayer.getBoundingClientRect();
            const matchedTextFlags = this.getMatchedTextElementFlags(textElements, normalizedQuery);

            for (let index = 0; index < textElements.length; index++) {
                const textElement = textElements[index] as HTMLElement;
                const rawText = (textElement.getAttribute('data-raw-text') || textElement.textContent || '');
                textElement.classList.remove('policy-search-hit-fallback');
                textElement.classList.remove('policy-search-hit-fallback-active');

                if (!hasQuery) {
                    continue;
                }

                if (!matchedTextFlags[index]) {
                    continue;
                }

                if (!searchLayer) {
                    textElement.classList.add('policy-search-hit-fallback');
                    this.highlightedMatches.push({
                        page: pageNumber,
                        element: textElement
                    });
                    continue;
                }

                const textRect = textElement.getBoundingClientRect();
                if (textRect.width > 0 && textRect.height > 0) {
                    const overlay = document.createElement('div');
                    overlay.className = 'policy-search-hit-overlay';
                    overlay.style.left = (textRect.left - textLayerRect.left) + 'px';
                    overlay.style.top = (textRect.top - textLayerRect.top) + 'px';
                    overlay.style.width = textRect.width + 'px';
                    overlay.style.height = textRect.height + 'px';
                    searchLayer.appendChild(overlay);

                    this.highlightedMatches.push({
                        page: pageNumber,
                        element: overlay
                    });
                    continue;
                }

                textElement.classList.add('policy-search-hit-fallback');
                this.highlightedMatches.push({
                    page: pageNumber,
                    element: textElement
                });
            }
        }

        this.searchMatches = this.highlightedMatches.map(matchRecord => matchRecord.page);
    }

    private navigateToCurrentSearchResult() {
        if (this.currentMatchIndex < 0 || this.currentMatchIndex >= this.searchMatches.length) {
            return;
        }

        const targetPage = this.searchMatches[this.currentMatchIndex];
        this.scrollToPage(targetPage);
        this.activateCurrentMatchOnPage(targetPage);
    }

    private activateCurrentMatchOnPage(pageNumber: number) {
        for (let index = 0; index < this.highlightedMatches.length; index++) {
            this.highlightedMatches[index].element.classList.remove('policy-search-hit-overlay-active');
            this.highlightedMatches[index].element.classList.remove('policy-link-hit-active');
            this.highlightedMatches[index].element.classList.remove('policy-search-hit-fallback-active');
        }

        const matchOnPage = this.highlightedMatches.find(matchRecord => matchRecord.page === pageNumber);
        if (!matchOnPage || !matchOnPage.element) {
            this.currentPage = pageNumber;
            return;
        }

        if (matchOnPage.element.classList.contains('policy-search-hit-overlay')) {
            matchOnPage.element.classList.add('policy-search-hit-overlay-active');
        }
        if (matchOnPage.element.classList.contains('policy-search-hit-fallback')) {
            matchOnPage.element.classList.add('policy-search-hit-fallback-active');
        }
        if (matchOnPage.element.tagName && matchOnPage.element.tagName.toLowerCase() === 'a') {
            matchOnPage.element.classList.add('policy-link-hit-active');
        }

        this.currentPage = pageNumber;

        setTimeout(() => {
            this.scrollElementIntoPdfView(matchOnPage.element);
        }, 120);
    }

    private getMatchedTextElementFlags(textElements: NodeListOf<Element>, normalizedQuery: string): boolean[] {
        const flags: boolean[] = [];
        for (let index = 0; index < textElements.length; index++) {
            flags.push(false);
        }

        if (!normalizedQuery) {
            return flags;
        }

        const normalizedTexts: string[] = [];
        for (let index = 0; index < textElements.length; index++) {
            const textElement = textElements[index] as HTMLElement;
            const textValue = (textElement.getAttribute('data-raw-text') || textElement.textContent || '').toLowerCase();
            normalizedTexts.push(textValue);
        }

        // First pass: direct match in single span
        for (let index = 0; index < normalizedTexts.length; index++) {
            const text = normalizedTexts[index] || '';
            if (text.indexOf(normalizedQuery) > -1) {
                flags[index] = true;
            }
        }

        return flags;
    }

    private normalizeForSearch(value: string): string {
        return (value || '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '');
    }

    private activateCurrentMatch(shouldScroll: boolean) {
        if (!this.highlightedMatches.length || this.currentMatchIndex < 0 || this.currentMatchIndex >= this.highlightedMatches.length) {
            return;
        }

        for (let index = 0; index < this.highlightedMatches.length; index++) {
            this.highlightedMatches[index].element.classList.remove('policy-search-hit-overlay-active');
            this.highlightedMatches[index].element.classList.remove('policy-link-hit-active');
            this.highlightedMatches[index].element.classList.remove('policy-search-hit-fallback-active');
        }

        const activeMatch = this.highlightedMatches[this.currentMatchIndex];
        if (!activeMatch || !activeMatch.element) {
            return;
        }

        if (activeMatch.element.classList.contains('policy-search-hit-overlay')) {
            activeMatch.element.classList.add('policy-search-hit-overlay-active');
        }
        if (activeMatch.element.classList.contains('policy-search-hit-fallback')) {
            activeMatch.element.classList.add('policy-search-hit-fallback-active');
        }
        if (activeMatch.element.tagName && activeMatch.element.tagName.toLowerCase() === 'a') {
            activeMatch.element.classList.add('policy-link-hit-active');
        }
        this.currentPage = activeMatch.page;

        if (!shouldScroll) {
            return;
        }

        this.scrollToPage(activeMatch.page);

        setTimeout(() => {
            this.scrollElementIntoPdfView(activeMatch.element);
        }, 120);
    }

    private scrollElementIntoPdfView(targetElement: HTMLElement) {
        if (!this.pdfContainer || !this.pdfContainer.nativeElement || !targetElement) {
            return;
        }

        const container: HTMLElement = this.pdfContainer.nativeElement;
        const containerRect = container.getBoundingClientRect();
        const targetRect = targetElement.getBoundingClientRect();
        const isAbove = targetRect.top < (containerRect.top + 32);
        const isBelow = targetRect.bottom > (containerRect.bottom - 32);

        if (!isAbove && !isBelow) {
            return;
        }

        const offset = targetRect.top - containerRect.top - (container.clientHeight * 0.35);
        const targetTop = container.scrollTop + offset;
        container.scrollTo({
            top: Math.max(0, targetTop),
            behavior: 'smooth'
        });
    }

    private escapeRegExp(value: string): string {
        return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    private getPdfLibrary(): any {
        const win = window as any;
        if (win && win.pdfjsDistBuildPdf) {
            return win.pdfjsDistBuildPdf;
        }
        if (win && win.PDFJS) {
            return win.PDFJS;
        }
        return null;
    }

    private handleHttpError(error: any) {
        console.error('Policy API error:', error);
        const statusCode = error && error.status;

        if (statusCode === 401) {
            this.errorMessage = 'Session expired. Please login again.';
            this.authenticationService.logout();
            return;
        }
        if (statusCode === 403) {
            this.errorMessage = 'You are not authorized to view this policy.';
            return;
        }
        if (statusCode === 404) {
            this.errorMessage = 'Policy not found.';
            return;
        }

        this.errorMessage = this.extractErrorMessage(error) || 'An unexpected error occurred.';
    }

    private extractErrorMessage(error: any): string {
        if (!error) {
            return '';
        }

        if (error._body) {
            try {
                const parsedBody = JSON.parse(error._body);
                if (parsedBody && parsedBody.message) {
                    return parsedBody.message;
                }
                if (parsedBody && parsedBody.errorMessage) {
                    return parsedBody.errorMessage;
                }
            } catch (e) {
                if (typeof error._body === 'string' && error._body.trim()) {
                    return error._body;
                }
            }
        }

        if (error.message) {
            return error.message;
        }

        return '';
    }

    private toggleOuterScrollLock(isEnabled: boolean) {
        const bodyElement = document && document.body ? document.body : null;
        if (!bodyElement || !bodyElement.classList) {
            return;
        }

        if (isEnabled) {
            bodyElement.classList.add('policy-viewer-scroll-lock');
            return;
        }

        bodyElement.classList.remove('policy-viewer-scroll-lock');
    }

    // Critical cleanup: release browser and PDF resources to prevent memory leaks.
    ngOnDestroy() {
        this.toggleOuterScrollLock(false);
        document.removeEventListener('contextmenu', this.contextMenuHandler);
        window.removeEventListener('keydown', this.keydownHandler, true);
        window.removeEventListener('resize', this.resizeHandler);

        if (this.pdfContainer && this.pdfContainer.nativeElement) {
            this.pdfContainer.nativeElement.removeEventListener('scroll', this.scrollHandler);
            this.pdfContainer.nativeElement.innerHTML = '';
        }

        if (this.pdfDoc && this.pdfDoc.destroy) {
            this.pdfDoc.destroy();
        }

        if (this.objectUrl) {
            URL.revokeObjectURL(this.objectUrl);
            this.objectUrl = null;
        }

        this.pdfDoc = null;
    }
}



// WEBPACK FOOTER //
// ./src/app/Components/PolicyViewer/policy-viewer.component.ts